import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../auth/clerk';
import { api } from '../../api/client';
import './BetaTipsCard.css';

const CHOICE_LABEL = 'Email me a few short beta tips from the Brainstormy team over the next two weeks. Unsubscribe any time.';

/**
 * One-time card for a writer created after this ships.
 * A missing beta_tips_card_seen means an older account, so the card stays hidden.
 * Continue records the checkbox either way.
 */
function BetaTipsCard() {
  const { isLoaded, isSignedIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [me, status] = await Promise.all([
          api.get('/users/me'),
          api.get('/users/me/beta-status'),
        ]);
        if (cancelled) return;
        setOpen(status?.beta_tips === true && me?.settings?.beta_tips_card_seen === false);
      } catch {
        if (!cancelled) setOpen(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  async function onContinue(event) {
    event.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await api.post('/users/me/beta-tips', { opt_in: checked });
      setOpen(false);
    } catch {
      savingRef.current = false;
      setSaving(false);
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="beta-tips-backdrop">
      <form
        className="beta-tips"
        role="dialog"
        aria-modal="true"
        aria-labelledby="beta-tips-choice"
        onSubmit={onContinue}
      >
        <label className="beta-tips__choice">
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => setChecked(event.target.checked)}
            autoFocus
          />
          <span id="beta-tips-choice">{CHOICE_LABEL}</span>
        </label>
        {/* Add the privacy policy link here once that page is published. */}
        <p className="beta-tips__fine">We'll use your sign-up email.</p>
        <div className="beta-tips__actions">
          <button type="submit" className="beta-tips__continue" disabled={saving}>
            Continue
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

export default BetaTipsCard;
